/**
 * Centro de notificaciones global (demo + localStorage).
 * Migración futura: reemplazar lectura/escritura por API sin cambiar la forma del ítem.
 */

const LS_KEY = 'spectra-rrhh-app-notifications-v1';
const MAX_ITEMS = 200;

/** @typedef {'all'|'unread'|'comms'|'tasks'|'leaves'|'docs'|'expenses'|'flow_ia'} NotificationFilter */

/**
 * @typedef {{
 *   id: string,
 *   type: string,
 *   title: string,
 *   body?: string,
 *   read: boolean,
 *   createdAt: string,
 *   priority?: 'high'|'medium'|'low',
 *   link?: string,
 *   ctaKey?: string,
 *   refId?: string,
 * }} AppNotification
 */

function safeParse(raw) {
  try {
    const v = JSON.parse(raw);
    return v && typeof v === 'object' ? v : {};
  } catch {
    return {};
  }
}

export function readStore() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    const o = safeParse(raw || '{}');
    const items = Array.isArray(o.items) ? o.items : [];
    return { items: items.filter((x) => x && x.id) };
  } catch {
    return { items: [] };
  }
}

function writeStore(items) {
  const trimmed = items.slice(0, MAX_ITEMS);
  localStorage.setItem(LS_KEY, JSON.stringify({ items: trimmed }));
  window.dispatchEvent(new CustomEvent('spectra-rrhh-notify-updated'));
}

function uid() {
  return `n_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/** @param {Omit<AppNotification, 'id'|'read'|'createdAt'> & Partial<Pick<AppNotification, 'id'|'read'|'createdAt'>>} payload */
export function addAppNotification(payload) {
  const { items } = readStore();
  const row = {
    id: payload.id || uid(),
    type: payload.type || 'generic',
    title: String(payload.title || '').slice(0, 200),
    body: payload.body != null ? String(payload.body).slice(0, 500) : '',
    read: Boolean(payload.read),
    createdAt: payload.createdAt || new Date().toISOString(),
    priority: payload.priority === 'high' || payload.priority === 'low' ? payload.priority : 'medium',
    link: payload.link || '',
    ctaKey: payload.ctaKey || '',
    refId: payload.refId != null && payload.refId !== '' ? String(payload.refId).slice(0, 80) : '',
  };
  writeStore([row, ...items.filter((x) => x.id !== row.id)]);
}

export function markRead(id) {
  const { items } = readStore();
  writeStore(items.map((x) => (x.id === id ? { ...x, read: true } : x)));
}

export function markAllRead() {
  const { items } = readStore();
  writeStore(items.map((x) => ({ ...x, read: true })));
}

/** @returns {AppNotification[]} */
export function seedDemoIfEmpty() {
  const { items } = readStore();
  if (items.length > 0) return items;
  const now = Date.now();
  const demo = [
    {
      id: 'seed_comms_1',
      type: 'comms',
      titleKey: 'notifyBell.seed.comms1Title',
      bodyKey: 'notifyBell.seed.comms1Body',
      read: false,
      createdAt: new Date(now - 4 * 60 * 1000).toISOString(),
      priority: 'medium',
      link: '/communications',
      ctaKey: 'notifyBell.ctaOpenComms',
    },
    {
      id: 'seed_docs_1',
      type: 'docs',
      titleKey: 'notifyBell.seed.docs1Title',
      bodyKey: 'notifyBell.seed.docs1Body',
      read: false,
      createdAt: new Date(now - 2 * 60 * 60 * 1000).toISOString(),
      priority: 'high',
      link: '/records',
      ctaKey: 'notifyBell.ctaReviewDoc',
    },
    {
      id: 'seed_leave_1',
      type: 'leaves',
      titleKey: 'notifyBell.seed.leave1Title',
      bodyKey: 'notifyBell.seed.leave1Body',
      read: true,
      createdAt: new Date(now - 26 * 60 * 60 * 1000).toISOString(),
      priority: 'low',
      link: '/leaves',
      ctaKey: 'notifyBell.ctaOpenLeave',
    },
    {
      id: 'seed_exp_1',
      type: 'expenses',
      titleKey: 'notifyBell.seed.exp1Title',
      bodyKey: 'notifyBell.seed.exp1Body',
      read: false,
      createdAt: new Date(now - 48 * 60 * 60 * 1000).toISOString(),
      priority: 'medium',
      link: '/expense-reports',
      ctaKey: 'notifyBell.ctaOpenExpense',
    },
    {
      id: 'seed_flow_1',
      type: 'flow_ia',
      titleKey: 'notifyBell.seed.flow1Title',
      bodyKey: 'notifyBell.seed.flow1Body',
      read: false,
      createdAt: new Date(now - 3 * 60 * 1000).toISOString(),
      priority: 'low',
      link: '/people-intelligence',
      ctaKey: 'notifyBell.ctaOpenFlowIa',
    },
    {
      id: 'seed_task_1',
      type: 'tasks',
      titleKey: 'notifyBell.seed.task1Title',
      bodyKey: 'notifyBell.seed.task1Body',
      read: false,
      createdAt: new Date(now - 20 * 60 * 1000).toISOString(),
      priority: 'high',
      link: '/employee-lifecycle',
      ctaKey: 'notifyBell.ctaOpenLifecycle',
    },
    {
      id: 'seed_train_1',
      type: 'training',
      titleKey: 'notifyBell.seed.train1Title',
      bodyKey: 'notifyBell.seed.train1Body',
      read: true,
      createdAt: new Date(now - 5 * 24 * 60 * 60 * 1000).toISOString(),
      priority: 'medium',
      link: '/training',
      ctaKey: 'notifyBell.ctaOpenTraining',
    },
    {
      id: 'seed_survey_1',
      type: 'surveys',
      titleKey: 'notifyBell.seed.survey1Title',
      bodyKey: 'notifyBell.seed.survey1Body',
      read: false,
      createdAt: new Date(now - 90 * 60 * 1000).toISOString(),
      priority: 'medium',
      link: '/surveys',
      ctaKey: 'notifyBell.ctaOpenSurvey',
    },
  ].map((x) => ({
    id: x.id,
    type: x.type,
    title: x.titleKey,
    body: x.bodyKey,
    read: x.read,
    createdAt: x.createdAt,
    priority: x.priority,
    link: x.link,
    ctaKey: x.ctaKey,
  }));
  writeStore(demo);
  return readStore().items;
}

/**
 * @param {AppNotification[]} items
 * @param {NotificationFilter} filter
 */
export function filterNotifications(items, filter) {
  const list = Array.isArray(items) ? items : [];
  if (filter === 'unread') return list.filter((x) => !x.read);
  if (filter === 'all') return list;
  if (filter === 'comms') return list.filter((x) => x.type === 'comms' || x.type === 'surveys');
  if (filter === 'tasks') return list.filter((x) => x.type === 'tasks' || x.type === 'training');
  if (filter === 'leaves') return list.filter((x) => x.type === 'leaves');
  if (filter === 'docs') return list.filter((x) => x.type === 'docs');
  if (filter === 'expenses') return list.filter((x) => x.type === 'expenses');
  if (filter === 'flow_ia') return list.filter((x) => x.type === 'flow_ia');
  return list;
}

export function unreadCount(items) {
  return items.filter((x) => !x.read).length;
}

export function notifyCommsPublished(t, title, communicationId) {
  addAppNotification({
    type: 'comms',
    priority: 'medium',
    title: t('notifyBell.sysPublishedTitle').replace('{title}', String(title || '—').slice(0, 120)),
    body: t('notifyBell.sysPublishedBody'),
    read: false,
    link: '/communications',
    ctaKey: 'notifyBell.ctaOpenComms',
    refId: communicationId != null && communicationId !== '' ? String(communicationId) : '',
  });
}


/**
 * Resuelve title/body si son claves i18n (empiezan con notifications.)
 * @param {(key: string) => string} t
 * @param {AppNotification} row
 */
export function resolveNotificationText(t, row) {
  const title = String(row.title || '');
  const body = String(row.body || '');
  const ti = title.startsWith('notifyBell.') ? t(title) : title;
  const bi = body.startsWith('notifyBell.') ? t(body) : body;
  return { title: ti, body: bi };
}
